'use client';

import { useEffect, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react';
import { PREVIEW_FILE, type MonacoEditModel, type MonacoEditor, type MonacoNamespace, type WorkspaceWithInternals, useA2UIMonacoRuntime } from './A2UIMonacoRuntimeProvider';
import { LOCAL_A2UI_PREVIEW_MONACO_JSON_SCHEMA } from './localA2UIPreviewMonacoSchema';

type ModernMonacoEditorProps = {
  value: string;
  onChange: (value: string) => void;
  ssrHtml?: string;
  readOnly?: boolean;
  onExternalValueApplied?: (value: string) => void;
  documentKey?: string;
};

type Disposable = { dispose: () => void };
type MonacoContentChange = { range: { startLineNumber: number; startColumn: number }; text: string };
export type A2UIPreviewPasteTransform = { text: string; replaceAll: boolean };
export type A2UIPreviewExternalSyncTextEdit = { startOffset: number; endOffset: number; text: string };
const MONACO_LANGS = ['json'];
const MONACO_LSP = { json: { schemas: [LOCAL_A2UI_PREVIEW_MONACO_JSON_SCHEMA] } };
const MONACO_RENDER_OPTIONS = {
  theme: 'vitesse-light',
  minimap: { enabled: true },
  scrollBeyondLastLine: false,
  smoothScrolling: true,
  roundedSelection: false,
  wordWrap: 'off',
  wrappingIndent: 'none',
  tabSize: 2,
  insertSpaces: true,
  padding: { top: 16, bottom: 16 },
  fontSize: 13,
  lineHeight: 20,
  fontLigatures: true,
  lineNumbers: 'interval',
  cursorBlinking: 'phase',
  renderLineHighlight: 'all',
  renderLineHighlightOnlyWhenFocus: true,
  stickyScroll: { enabled: true },
  guides: { indentation: false, highlightActiveIndentation: false, bracketPairs: false, bracketPairsHorizontal: false, highlightActiveBracketPair: false },
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
} as const;
const normalizeLineEndings = (value: string) => value.replace(/\r\n?/g, '\n');
const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const hasOwn = (value: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(value, key);
const looksLikeJsonContainer = (value: string) => {
  const trimmed = value.trim();
  return (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
};
const decodeSingleQuotedString = (value: string) => value.replace(/\\r/g, '\r').replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\"/g, '"').replace(/\\'/g, "'").replace(/\\\\/g, '\\');
const sanitizeDocumentSegment = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'default';
const toFileUri = (filename: string) => `file:///${filename.replace(/^\/+/, '')}`;
const getDocumentFilename = (documentKey: string) => `${documentKey.split(':').map(sanitizeDocumentSegment).join('--') || 'preview-default'}--${PREVIEW_FILE}`;
const getA2UIPreviewClientOnlyMonacoHtml = (filename: string, code: string) =>
  `<monaco-editor><script type="application/json" class="monaco-editor-options">${JSON.stringify([{ filename, code }, MONACO_RENDER_OPTIONS]).replaceAll('</', '<\\/')}</script></monaco-editor>`; // The SSR/lazy contract wants a real <monaco-editor> element in the HTML up front; if we render anything else first, modern-monaco cannot upgrade it in place and the user sees a blank/flickering swap.

export function shouldIgnoreA2UIPreviewWorkspaceWatchSync({ hasDirectModel, isReadOnly, isSyncing, isModelContentChange }: { hasDirectModel: boolean; isReadOnly: boolean; isSyncing: boolean; isModelContentChange: boolean }) {
  return isSyncing || isReadOnly || hasDirectModel || !isModelContentChange;
}

function unwrapQuotedString(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length < 2) return null;
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === 'string' ? parsed.trim() : null;
    } catch {
      return null;
    }
  }
  if (trimmed.startsWith("'") && trimmed.endsWith("'")) return decodeSingleQuotedString(trimmed.slice(1, -1)).trim();
  return null;
}

function parsePastedJsonValue(value: string): Record<string, unknown> | unknown[] | null {
  let candidate: string | null = value.trim();
  const seen = new Set<string>();
  for (let depth = 0; candidate && depth < 4 && !seen.has(candidate); depth += 1) {
    seen.add(candidate);
    if (looksLikeJsonContainer(candidate)) {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed) || isRecord(parsed)) return parsed;
      } catch {
        // Keep probing looser pasted-string shapes; invalid intermediate candidates are expected while we unwrap quoted JSON payloads.
      }
    }
    candidate = unwrapQuotedString(candidate);
  }
  return null;
}

function isRenderablePayload(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  return Array.isArray(value.messages) || Array.isArray(value.objects);
}

function isA2UIMessageObject(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  return hasOwn(value, 'beginRendering') || hasOwn(value, 'surfaceUpdate') || hasOwn(value, 'dataModelUpdate') || hasOwn(value, 'deleteSurface');
}

function normalizeA2UIWrapperValue(value: unknown): Record<string, unknown> | unknown[] | null {
  if (Array.isArray(value) || isRenderablePayload(value) || isA2UIMessageObject(value)) return value;
  if (typeof value !== 'string') return null;
  const parsed = parsePastedJsonValue(value);
  return Array.isArray(parsed) || isRenderablePayload(parsed) || isA2UIMessageObject(parsed) ? parsed : null;
}

function normalizePastedPreviewObject(value: Record<string, unknown>) {
  if (!hasOwn(value, 'a2ui')) return value;
  const normalizedA2UI = normalizeA2UIWrapperValue(value.a2ui);
  if (!normalizedA2UI) return value;
  const { a2ui: _a2ui, ...rest } = value;
  if (Array.isArray(normalizedA2UI)) return { ...rest, payload: { objects: normalizedA2UI } };
  if (isRenderablePayload(normalizedA2UI)) return { ...rest, payload: normalizedA2UI };
  return { ...rest, payload: { objects: [normalizedA2UI] } };
}

export function isCompleteA2UIPreviewObject(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const nestedSurface = isRecord(value.canvas_surface) ? value.canvas_surface : null;
  return (
    isRenderablePayload(value.payload) ||
    Array.isArray(value.messages) ||
    Array.isArray(value.objects) ||
    Array.isArray(value.a2ui_messages) ||
    Boolean(nestedSurface && (isRenderablePayload(nestedSurface.payload) || Array.isArray(nestedSurface.messages) || Array.isArray(nestedSurface.objects) || Array.isArray(nestedSurface.a2ui_messages)))
  );
}

function isCompleteA2UIPreviewRoot(value: unknown): boolean {
  return isCompleteA2UIPreviewObject(value) || (Array.isArray(value) && value.every(isA2UIMessageObject));
}

function unwrapTopLevelPreviewOutput(value: unknown): unknown {
  if (!isRecord(value) || !hasOwn(value, 'output')) return value;
  return isCompleteA2UIPreviewRoot(value.output) ? value.output : value;
}

export function getA2UIPreviewPasteTransform(value: string): A2UIPreviewPasteTransform | null {
  const parsed = parsePastedJsonValue(value);
  if (!parsed) return null;
  const candidate = isRecord(parsed) ? unwrapTopLevelPreviewOutput(parsed) : parsed;
  const normalized = isRecord(candidate) ? normalizePastedPreviewObject(candidate) : candidate;
  return { text: JSON.stringify(normalized, null, 2), replaceAll: isCompleteA2UIPreviewRoot(normalized) };
}

export function getA2UIPreviewReplaceAllPasteText(value: string): string | null {
  const transform = getA2UIPreviewPasteTransform(value);
  return transform?.replaceAll ? transform.text : null;
}

function getInsertedRange(monaco: MonacoNamespace, change: MonacoContentChange) {
  const insertedLines = normalizeLineEndings(change.text).split('\n');
  const endLineNumber = change.range.startLineNumber + insertedLines.length - 1;
  const endColumn = insertedLines.length === 1 ? change.range.startColumn + insertedLines[0].length : insertedLines.at(-1)!.length + 1;
  return new monaco.Range(change.range.startLineNumber, change.range.startColumn, endLineNumber, endColumn);
}

export function getA2UIPreviewExternalSyncTextEdit(previousValue: string, nextValue: string): A2UIPreviewExternalSyncTextEdit | null {
  if (previousValue === nextValue) return null;
  let startOffset = 0;
  const maxPrefixLength = Math.min(previousValue.length, nextValue.length);
  while (startOffset < maxPrefixLength && previousValue.charCodeAt(startOffset) === nextValue.charCodeAt(startOffset)) startOffset += 1;
  let previousEndOffset = previousValue.length;
  let nextEndOffset = nextValue.length;
  while (previousEndOffset > startOffset && nextEndOffset > startOffset && previousValue.charCodeAt(previousEndOffset - 1) === nextValue.charCodeAt(nextEndOffset - 1)) {
    previousEndOffset -= 1;
    nextEndOffset -= 1;
  }
  return { startOffset, endOffset: previousEndOffset, text: nextValue.slice(startOffset, nextEndOffset) };
}

function isEditorOwnedByElement(editor: MonacoEditor, host: HTMLElement) {
  // The shared runtime can have multiple editors over time; without this ownership check, we can accidentally subscribe to some stale/other page instance.
  try {
    const container = editor.getContainerDomNode();
    const root = container.getRootNode();
    return root instanceof ShadowRoot ? root.host === host : host.contains(container);
  } catch {
    return false;
  }
}

function removeLingeringPrerender(host: HTMLDivElement) {
  // SSR prerender must disappear only after the live editor is ready; leaving it around duplicates visible code and can push the Monaco canvas out of view.
  host.querySelectorAll('.monaco-editor-prerender').forEach((node) => node.remove());
  host.querySelectorAll('pre, code').forEach((node) => {
    if (node.closest('.monaco-editor')) return;
    if (node.closest('monaco-editor')) return;
    node.remove();
  });
}

async function ensureWorkspaceDocument(workspace: WorkspaceWithInternals, filename: string, value: string) {
  // Centralize file->model resolution so every caller uses the same workspace/model identity rules; ad hoc open/create logic was a major source of drift before.
  const href = toFileUri(filename);
  const monaco = await workspace._monaco!.promise;
  const model = monaco.editor.getModel(monaco.Uri.parse(href)) as MonacoEditModel | null;
  if (model) return model;
  await workspace.fs.writeFile(filename, value, { isModelContentChange: false });
  return (await workspace.openTextDocument(filename)) as MonacoEditModel;
}

function restoreEditorModel(editor: MonacoEditor, targetModel: MonacoEditModel, modelViewStateCache: Map<string, ReturnType<MonacoEditor['saveViewState']>>) {
  // Rebinding one editor to another model is the safe/reference approach; remounting the editor loses UX state and reintroduces flicker.
  const currentModel = editor.getModel() as MonacoEditModel | null;
  if (currentModel === targetModel) return;
  if (currentModel) {
    const previousViewState = editor.saveViewState();
    if (previousViewState) modelViewStateCache.set(currentModel.uri.toString(), previousViewState);
  }
  editor.setModel(targetModel);
  const savedViewState = modelViewStateCache.get(targetModel.uri.toString());
  if (savedViewState) editor.restoreViewState(savedViewState);
}

export default function ModernMonacoEditor({ value, onChange, ssrHtml, readOnly = false, onExternalValueApplied, documentKey = 'preview:default' }: ModernMonacoEditorProps) {
  const { ensureWorkspace, loadModernMonaco, modelViewStateCache } = useA2UIMonacoRuntime();
  const containerRef = useRef<HTMLDivElement | null>(null); // The outer host stays stable across React renders so modern-monaco can upgrade the same DOM slot instead of chasing a moving target.
  const editorRef = useRef<MonacoEditor | null>(null); // Keep the live Monaco editor outside React state; putting it in state would rerender the page on every attach/detach and worsen mount churn.
  const workspaceRef = useRef<WorkspaceWithInternals | null>(null); // Shared workspace handle is cached in a ref because async Monaco callbacks need the latest object without retriggering effects.
  const elementRef = useRef<HTMLElement | null>(null); // Track the upgraded custom element so cleanup removes listeners from the right host and does not stomp SSR DOM unnecessarily.
  const onChangeRef = useRef(onChange);
  const onExternalValueAppliedRef = useRef(onExternalValueApplied);
  const readOnlyRef = useRef(readOnly);
  const codeRef = useRef(value); // React props can lag behind Monaco events inside async callbacks; this ref keeps the current source text available without stale-closure bugs.
  const filenameRef = useRef(getDocumentFilename(documentKey)); // Same idea for the logical document id: async route/model work must always target the latest filename, not the one from effect creation time.
  const latestValueRef = useRef(value); // We use this to suppress feedback loops between Monaco -> React -> Monaco; removing it causes redundant writes and visible “snap back” while typing.
  const pendingPasteRef = useRef<string | null>(null); // Paste normalization is multi-step and crosses capture/content-change phases, so we need to stash the raw clipboard text transiently outside React.
  const syncingRef = useRef(false); // This flag distinguishes our own model writes from user edits; without it, external sync/format operations echo back as fake user changes and create loops.
  const initialInnerHtmlRef = useRef({ __html: ssrHtml ?? getA2UIPreviewClientOnlyMonacoHtml(getDocumentFilename(documentKey), value) }); // Freeze the initial SSR shell once so hydration sees a stable DOM contract; recomputing this during renders risks hydration mismatches and DOM churn.
  const [hasLoadError, setHasLoadError] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const filename = getDocumentFilename(documentKey);

  onChangeRef.current = onChange;
  onExternalValueAppliedRef.current = onExternalValueApplied;
  readOnlyRef.current = readOnly;
  codeRef.current = value;
  filenameRef.current = filename;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    let editorDispose: Disposable | null = null;
    let creationDispose: Disposable | null = null;
    let prerenderCleanupAnimationFrame: number | null = null;
    let prerenderCleanupTimeout: number | null = null;
    const applyModelEdits = (model: MonacoEditModel, edits: { range: unknown; text: string }[]) => {
      syncingRef.current = true;
      try {
        model.pushStackElement?.();
        model.applyEdits(edits);
        model.pushStackElement?.();
        const nextValue = model.getValue();
        latestValueRef.current = nextValue;
        onChangeRef.current(nextValue);
      } finally {
        syncingRef.current = false;
      }
    };
    const applyPasteTransform = (monaco: MonacoNamespace, editor: MonacoEditor, event: { changes: readonly MonacoContentChange[] }) => {
      const pendingPaste = pendingPasteRef.current;
      pendingPasteRef.current = null;
      if (!pendingPaste) return false;
      const transform = getA2UIPreviewPasteTransform(pendingPaste);
      const model = editor.getModel() as MonacoEditModel | null;
      const firstChange = event.changes.length === 1 ? event.changes[0] : null;
      if (!transform || !model) return false;
      if (transform.replaceAll) {
        if (transform.text === model.getValue()) return false;
        applyModelEdits(model, [{ range: model.getFullModelRange(), text: transform.text }]);
        return true;
      }
      if (!firstChange || normalizeLineEndings(firstChange.text) !== normalizeLineEndings(pendingPaste) || normalizeLineEndings(firstChange.text) === normalizeLineEndings(transform.text)) return false;
      applyModelEdits(model, [{ range: getInsertedRange(monaco, firstChange), text: transform.text }]);
      return true;
    };
    const handlePasteCapture = (event: Event) => {
      if (readOnlyRef.current) {
        event.preventDefault();
        event.stopPropagation();
        pendingPasteRef.current = null;
        return;
      }
      const pastedText = (event as ClipboardEvent).clipboardData?.getData('text') ?? null;
      pendingPasteRef.current = pastedText;
      if (!pastedText) return;
      const replaceAllText = getA2UIPreviewReplaceAllPasteText(pastedText);
      if (!replaceAllText) return;
      const model = editorRef.current?.getModel() as MonacoEditModel | null;
      if (!model) return;
      event.preventDefault();
      event.stopPropagation();
      pendingPasteRef.current = null;
      applyModelEdits(model, [{ range: model.getFullModelRange(), text: replaceAllText }]); // Full preview payload pastes should replace the whole document no matter where the caret is; waiting for Monaco's later content-change diff can miss that intent after runtime/editor refactors.
    };
    const blockReadOnlyInput = (event: Event) => {
      if (!readOnlyRef.current) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const syncEditorToDocument = async (nextValue = codeRef.current, nextFilename = filenameRef.current) => {
      const workspace = workspaceRef.current;
      const editor = editorRef.current;
      if (!workspace || !editor) return;
      const targetModel = await ensureWorkspaceDocument(workspace, nextFilename, nextValue); // Always resolve the target file through the workspace so route changes switch models instead of recreating editors; bypassing this is what previously made CSR drift or show the wrong document.
      if (disposed || workspaceRef.current !== workspace || editorRef.current !== editor) return; // Async Monaco work often completes after navigation; without this stale-guard, an old page can still rebind the visible editor and cause the “jump back to previous JSON” bug.
      restoreEditorModel(editor, targetModel, modelViewStateCache); // Preserve one editor instance and swap only its model/view-state; remounting the editor here reintroduces flicker and loses cursor/scroll position on every example change.
      editor.updateOptions({ readOnly: readOnlyRef.current }); // Read-only must update on the live editor rather than by rebuilding it; rebuilding for play mode caused extra mount churn and made the SSR→Monaco transition less stable.
      const targetHref = toFileUri(nextFilename);
      if (workspace.history.state.current !== targetHref) workspace.history.push(targetHref); // Keep workspace navigation aligned with the visible file so background dependency opens stay detached; if history points at the wrong file, modern-monaco can retarget the visible editor unexpectedly.
      if (targetModel.getValue() === nextValue) {
        latestValueRef.current = nextValue;
        onExternalValueAppliedRef.current?.(nextValue);
        return;
      }
      syncingRef.current = true;
      try {
        targetModel.setValue(nextValue); // External source-of-truth updates must land on the already-selected model; replacing the model/editor instead of mutating it is exactly what caused visible flashing and stale content races before.
        latestValueRef.current = nextValue;
      } finally {
        syncingRef.current = false;
      }
      onExternalValueAppliedRef.current?.(nextValue);
    };
    void (async () => {
      try {
        const workspace = await ensureWorkspace(filenameRef.current, codeRef.current); // The workspace is the durable document registry across /a2ui-preview and /a2ui-gallery; if each page made its own workspace, CSR would lose model identity and Format/edit sync would break again.
        const { lazy } = await loadModernMonaco();
        await workspace.fs.writeFile(filenameRef.current, codeRef.current, { isModelContentChange: false }); // Seed the file before the custom element upgrades so the SSR prerender and the eventual Monaco model point at the same text; otherwise the UI briefly paints one document and then snaps to another.
        if (disposed || !containerRef.current) return; // If the route unmounted while Monaco was loading, we must stop here; continuing would attach listeners/editors into a dead page and recreate the old oscillation bug.
        workspaceRef.current = workspace;
        const editorElement = (container.querySelector('monaco-editor') as HTMLElement | null) ?? document.createElement('monaco-editor'); // Reuse the SSR-rendered custom element whenever it exists; recreating/replacing it throws away the prerender DOM and causes the first-open flicker the user reported.
        editorElement.className = 'block h-full w-full'; // Keep layout on the element itself so the upgraded editor occupies the exact SSR box; sizing it later on nested nodes causes visible height jumps during hydration.
        const bootstrapScript = (editorElement.querySelector('.monaco-editor-options') as HTMLScriptElement | null) ?? document.createElement('script'); // The options script is the handoff contract modern-monaco reads when upgrading the custom element; if we duplicate this node or forget to update it, the wrong file can open after navigation.
        bootstrapScript.className = 'monaco-editor-options';
        bootstrapScript.type = 'application/json';
        bootstrapScript.textContent = JSON.stringify([
          { filename: filenameRef.current, code: codeRef.current },
          { ...MONACO_RENDER_OPTIONS, language: 'json', theme: MONACO_RENDER_OPTIONS.theme, readOnly: readOnlyRef.current },
        ]); // We overwrite the payload in place so the existing SSR element upgrades with the latest document/readOnly state; rebuilding the whole element here is what previously introduced the visible swap.
        if (!bootstrapScript.parentElement) editorElement.appendChild(bootstrapScript); // Only append when missing so we preserve the SSR element shape; appending a second script or recreating the subtree makes upgrade behavior nondeterministic.
        elementRef.current = editorElement;
        if (editorElement.parentElement !== container) container.replaceChildren(editorElement); // Only move the element if it is not already mounted here; unconditional replaceChildren() was enough to force an unnecessary SSR→blank→Monaco transition.
        lazy({ workspace, defaultTheme: MONACO_RENDER_OPTIONS.theme, langs: MONACO_LANGS, lsp: MONACO_LSP }); // Call lazy for every new host element. TanStack route navigation can keep the shared runtime alive while swapping in a fresh preview <monaco-editor>; skipping lazy in that state leaves only the prerendered code block visible.
        editorElement.addEventListener('paste', handlePasteCapture, true); // These listeners must live on the custom element host so they survive Monaco’s internal DOM churn; binding deeper in the tree misses events after upgrade.
        editorElement.addEventListener('beforeinput', blockReadOnlyInput, true); // Block read-only mutations at capture phase before Monaco handles them; relying on later guards lets transient edits leak into the model during play mode.
        editorElement.addEventListener('keydown', blockReadOnlyInput, true); // Same reason as beforeinput: keystrokes must be stopped before Monaco mutates the model, or the preview/playback state can desync.
        const monaco = await workspace._monaco!.promise;
        if (disposed) return; // Monaco can finish booting after navigation; without this second stale-guard, a dead page can still subscribe to the shared runtime and steal updates from the current page.
        const attachEditor = (candidate: MonacoEditor) => {
          if (!isEditorOwnedByElement(candidate, editorElement)) return false; // Only attach to the editor created for this specific custom element; grabbing any editor from the shared runtime is how we previously ended up with multiple live instances cross-talking.
          editorRef.current = candidate;
          candidate.updateOptions({ readOnly: readOnlyRef.current }); // Normalize options on the real editor immediately after ownership is known; otherwise the upgraded editor can briefly accept input before the readOnly effect catches up.
          editorDispose = candidate.onDidChangeModelContent((event) => {
            if (syncingRef.current || readOnlyRef.current) return;
            if (applyPasteTransform(monaco, candidate, event as { changes: readonly MonacoContentChange[] })) return;
            const nextValue = (candidate.getModel() as MonacoEditModel | null)?.getValue();
            if (typeof nextValue !== 'string' || nextValue === latestValueRef.current) return;
            latestValueRef.current = nextValue;
            codeRef.current = nextValue;
            onChangeRef.current(nextValue); // User typing must flow straight back to React state from the live model; waiting for indirect workspace watchers here caused token count/preview to update while the visible code lagged behind.
          });
          return true;
        };
        for (const candidate of monaco.editor.getEditors() as MonacoEditor[]) {
          if (attachEditor(candidate)) break;
        }
        if (!editorRef.current) {
          const created = monaco.editor.onDidCreateEditor((candidate) => {
            if (!attachEditor(candidate as MonacoEditor)) return;
            created.dispose();
          });
          creationDispose = created;
        }
        const model = await ensureWorkspaceDocument(workspace, filenameRef.current, codeRef.current); // Ensure the target model exists before first bind so the upgraded editor never falls back to an old/default entry file.
        if (disposed) return;
        if (model.getValue() !== codeRef.current) model.setValue(codeRef.current); // Keep the model’s text aligned with React before the editor becomes interactive; skipping this lets SSR prerender show the new file while Monaco upgrades with stale IndexedDB content.
        await syncEditorToDocument(); // Explicitly bind the visible editor to the requested document after startup; if we leave this to implicit workspace state, first-open and CSR can land on whatever model Monaco touched most recently.
        if (disposed) return;
        removeLingeringPrerender(container); // Remove the SSR prerender only after the real editor/model are ready; doing it sooner causes a blank flash, doing it later leaves stacked text under/over Monaco.
        prerenderCleanupAnimationFrame = window.requestAnimationFrame(() => removeLingeringPrerender(container)); // Repeat on the next frame because modern-monaco may append prerender DOM during upgrade; a single synchronous cleanup was not always enough on localhost.
        prerenderCleanupTimeout = window.setTimeout(() => removeLingeringPrerender(container), 250); // And repeat once more after a short delay to catch slower upgrade timing; without this belt-and-suspenders cleanup, stale prerender nodes sometimes stayed in normal flow and visually pushed Monaco around.
      } catch (error) {
        console.error('Failed to initialize A2UI Monaco editor:', error);
        if (!disposed) {
          setHasLoadError(true);
          setLoadErrorMessage(error instanceof Error ? error.message : String(error));
        }
      }
    })();
    return () => {
      disposed = true;
      pendingPasteRef.current = null;
      if (prerenderCleanupAnimationFrame !== null) window.cancelAnimationFrame(prerenderCleanupAnimationFrame);
      if (prerenderCleanupTimeout !== null) window.clearTimeout(prerenderCleanupTimeout);
      creationDispose?.dispose(); // Tear down the temporary onDidCreateEditor listener so future editors from other pages do not get accidentally claimed by this unmounted component.
      editorDispose?.dispose(); // Remove the live model subscription on unmount; leaking it means stale pages still push edits back into React state after navigation.
      editorRef.current = null;
      workspaceRef.current = null;
      elementRef.current?.removeEventListener('paste', handlePasteCapture, true);
      elementRef.current?.removeEventListener('beforeinput', blockReadOnlyInput, true);
      elementRef.current?.removeEventListener('keydown', blockReadOnlyInput, true);
      elementRef.current = null; // Intentionally do not remove/recreate the SSR custom element here; resetting the DOM in cleanup was one of the main causes of single-page open flicker.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount should initialize Monaco exactly once for this editor host; the runtime methods/cache come from a module-level singleton provider and are intentionally treated as stable.
  }, []);

  useEffect(() => {
    editorRef.current?.updateOptions({ readOnly });
  }, [readOnly]);

  useEffect(() => {
    if (!workspaceRef.current || !editorRef.current) return;
    let cancelled = false;
    void (async () => {
      const workspace = workspaceRef.current;
      const editor = editorRef.current;
      if (!workspace || !editor) return;
      const targetModel = await ensureWorkspaceDocument(workspace, filename, value); // Route/value changes should reuse the current workspace document registry, not spin up new Monaco state, or client navigation will regress into remount-like behavior.
      if (cancelled || workspaceRef.current !== workspace || editorRef.current !== editor) return; // Ignore stale async completions from the previous slug/value so they cannot overwrite the current page after fast navigation.
      restoreEditorModel(editor, targetModel, modelViewStateCache); // Model switching is the safe path; recreating the editor here would bring back the same flicker and cursor-reset issues that prompted this whole refactor.
      editor.updateOptions({ readOnly: readOnlyRef.current }); // Keep option updates local to the live editor instead of rebuilding anything around it.
      editor.layout(); // Force layout after model switches so Monaco remeasures against the current workbench size; skipping this can leave temporarily wrong geometry after route transitions.
      const targetHref = toFileUri(filename);
      if (workspace.history.state.current !== targetHref) workspace.history.push(targetHref);
      if (targetModel.getValue() === value || (editor.getModel()?.uri.toString() === toFileUri(filename) && latestValueRef.current === value)) {
        // If the visible model already reflects React state, bail early; writing again here causes needless model churn and can show a subtle “flash back” while typing/formatting.
        latestValueRef.current = value;
        onExternalValueAppliedRef.current?.(value);
        return;
      }
      syncingRef.current = true;
      try {
        targetModel.setValue(value); // Apply external updates to the current model in place so Format and imported-seed updates remain visible immediately; swapping editor instances here previously made token count change while the visible code stayed stale.
        latestValueRef.current = value;
      } finally {
        syncingRef.current = false;
      }
      onExternalValueAppliedRef.current?.(value);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `modelViewStateCache` is a stable singleton-runtime Map; this resync should respond only to the incoming document identity/value.
  }, [filename, value]);

  const handleFallbackPaste = (event: ReactClipboardEvent<HTMLTextAreaElement>) => {
    if (readOnly) {
      event.preventDefault();
      return;
    }
    const transform = getA2UIPreviewPasteTransform(event.clipboardData.getData('text'));
    if (!transform) return;
    event.preventDefault();
    if (transform.replaceAll) return onChange(transform.text);
    const { selectionStart, selectionEnd, value: currentValue } = event.currentTarget;
    onChange(currentValue.slice(0, selectionStart) + transform.text + currentValue.slice(selectionEnd));
  };

  if (hasLoadError)
    return (
      <textarea
        className="h-full min-h-[360px] w-full resize-none border-0 bg-white p-4 font-mono text-[13px] leading-5 outline-none"
        value={value}
        spellCheck={false}
        readOnly={readOnly}
        onChange={(event) => onChange(event.target.value)}
        onPaste={handleFallbackPaste}
        aria-label={loadErrorMessage ? `A2UI preview JSON editor fallback: ${loadErrorMessage}` : 'A2UI preview JSON editor fallback'}
      />
    );

  return (
    <div className="relative h-full min-h-[360px] w-full overflow-hidden bg-white">
      <div ref={containerRef} className="a2ui-modern-monaco h-full w-full overflow-hidden bg-white" suppressHydrationWarning dangerouslySetInnerHTML={initialInnerHtmlRef.current} />{' '}
      {/* Keep `a2ui-modern-monaco`: app/globals.css scopes the sticky-scroll color overrides to this class, so removing it silently falls back to Monaco's default gray chrome. */}
      {readOnly ? <div className="absolute inset-0 z-10 cursor-progress bg-white/0" aria-hidden="true" /> : null}
    </div>
  );
}
